import axios from 'axios';
import { moment } from '@hotjar/webapp/lib/moment';
import { bus, siteLastDataRefreshed } from '@hotjar/webapp/utils/events';
import type { Site } from '@hotjar/site';
import type { SiteLastDataResponseDto } from './dto';
import type { LastData, TrackingCodeState } from './types';

/**
 * Get an object for last_data which include two properties:
 * 1) hoursSince: The # of hours since we last received data.
 * 2) timestamp: returns site.last_data or null is undefined.
 *
 * @param site Site to get lastData info for.
 * @return {object} Object containing hoursSince and timestamp.
 */
function getLastData(timestamp: Site['lastData']): LastData {
  if (timestamp) {
    return {
      hoursSince: moment().diff(moment.unix(timestamp), 'hours'),
      timestamp,
    };
  }

  return {
    hoursSince: null,
    timestamp: null,
  };
}

/**
 * Get the state of the tracking code for a site.
 *
 * @param site {object} Site to get state for.
 * @return {object} Object include tracking state info.
 */
export function getState(siteLastData: Site['lastData']): TrackingCodeState {
  const { timestamp, hoursSince } = getLastData(siteLastData);
  const installed = timestamp !== null;
  const isActive = installed && hoursSince !== null && hoursSince < 24;

  // Installed: True if at some point in the past, the tracking code was installed.
  // isActive: True if data was collected traffic in the past 24 hours.
  // lastData: Object containing the time stamp and hour since last data.
  return {
    installed,
    isActive,
    lastData: {
      hoursSince,
      timestamp,
    },
  };
}

function getEndpointPath(accountId: number, siteId: number): string {
  return `/api/v1/accounts/${accountId}/sites/${siteId}`;
}

/**
 * Get the state of the tracking code for a site from the server.
 *
 * @param site {object} Site to get state for.
 * @return {object} Object include tracking state info.
 */
export async function getStateFromServer(site: Site): Promise<TrackingCodeState> {
  const { account } = site;
  const response: SiteLastDataResponseDto = await axios.get(
    getEndpointPath(account.id as number, site.id),
  );

  bus.publish(siteLastDataRefreshed({ site, state: response.data }));

  return getState(response.data.last_data);
}
